"use client";

import Image from "next/image";
import { LazyMotion, domAnimation, m } from "framer-motion";
import Breadcrumbs, { type BreadcrumbItem } from "@/components/Breadcrumbs";
import CtaButton from "@/components/ui/CtaButton";

type ImageProps = {
    src: string;
    alt?: string;
};

const SIZES_FIRST = "(max-width: 767px) 85vw, (max-width: 1023px) 280px, 420px";
const SIZES_SECOND = "(max-width: 767px) 85vw, (max-width: 1023px) 520px, 900px";

type Props = {
    breadcrumbs?: BreadcrumbItem[];
    heading: string;
    description: string;
    firstImage: ImageProps;
    secondImage: ImageProps;
};

export type HeroLocationProps = React.ComponentPropsWithoutRef<"section"> & Partial<Props>;

const slideUp = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

export const HeroLocation = (props: HeroLocationProps) => {
    const { breadcrumbs, heading, description, firstImage, secondImage } = {
        ...HeroLocationDefaults,
        ...props,
    };
    return (
        <LazyMotion features={domAnimation}>
            <m.section
                id="relume"
                initial="hidden"
                animate="visible"
                className="px-[5%] pt-28 pb-10 md:pt-36 md:pb-14 lg:pt-44 lg:pb-18"
            >
                <div className="container">
                    <div className="rb-12 mb-4 grid grid-cols-1 items-start gap-5 md:mb-5 md:grid-cols-2 md:gap-12 lg:mb-6 lg:gap-20">
                        <div>
                            {breadcrumbs && breadcrumbs.length > 0 && (
                                <m.div variants={slideUp} custom={0} className="mb-4 md:mb-6 will-change-transform">
                                    <Breadcrumbs items={breadcrumbs} />
                                </m.div>
                            )}
                            <m.h1
                                variants={slideUp}
                                custom={breadcrumbs && breadcrumbs.length > 0 ? 1 : 0}
                                className="text-6xl font-medium md:text-7xl will-change-transform"
                            >
                                {heading}
                            </m.h1>
                        </div>
                        <m.div
                            variants={slideUp}
                            custom={breadcrumbs && breadcrumbs.length > 0 ? 2 : 1}
                            className="mr-[7.5%] flex flex-col justify-end md:mt-48 md:-ml-12 md:w-[calc(100%+3rem)] lg:-ml-20 lg:w-[calc(100%+5rem)] will-change-transform"
                        >
                            <p className="md:text-md">{description}</p>
                            <div className="mt-6 flex flex-wrap gap-4 md:mt-8">
                                <CtaButton />
                            </div>
                        </m.div>
                    </div>

                    <div className="mt-12 grid grid-cols-1 items-start gap-6 sm:gap-8 md:mt-0 md:grid-cols-[0.33fr_1fr] md:gap-16">
                        <m.div
                            variants={slideUp}
                            custom={breadcrumbs && breadcrumbs.length > 0 ? 3 : 2}
                            className="relative aspect-[4/5] w-full max-w-xs mx-auto will-change-transform overflow-hidden rounded-3xl md:aspect-[2/3] md:max-w-none md:mx-0 md:-mt-16 lg:-mt-20"
                        >
                            <Image
                                src={firstImage.src}
                                alt={firstImage.alt ?? ""}
                                fill
                                sizes={SIZES_FIRST}
                                priority
                                className="object-cover"
                            />
                        </m.div>
                        <m.div
                            variants={slideUp}
                            custom={breadcrumbs && breadcrumbs.length > 0 ? 4 : 3}
                            className="relative aspect-[3/2] w-full will-change-transform overflow-hidden rounded-3xl md:mt-[10%]"
                        >
                            <Image
                                src={secondImage.src}
                                alt={secondImage.alt ?? ""}
                                fill
                                sizes={SIZES_SECOND}
                                loading="lazy"
                                className="object-cover"
                            />
                        </m.div>
                    </div>
                </div>
            </m.section>
        </LazyMotion>
    );
};

export const HeroLocationDefaults: Props = {
    heading: "Medium length hero heading goes here",
    description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat.",
    firstImage: {
        src: "https://d22po4pjz3o32e.cloudfront.net/placeholder-image.svg",
        alt: "Relume placeholder image 1",
    },
    secondImage: {
        src: "https://d22po4pjz3o32e.cloudfront.net/placeholder-image-landscape.svg",
        alt: "Relume placeholder image 2",
    },
};
